import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  PlusCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  TrashIcon,
  LightningIcon,
} from '@phosphor-icons/react'
import { useLocation, useNavigate, useMatch } from 'react-router-dom'

import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { useCaptureEvent } from '@/hooks'
import { isProductionBuild } from '@/utils/environment'

import { selectAmenityById } from '../../../reducers/Organisation/reservationsReducer'

import ToolbarMenu from './ToolbarMenu'

const NEW_RESERVATIONS_PATH = '/reservations/bookings'
const NEW_AMENITIES_PATH = '/reservations/amenities'

const ReservationsToolbar = ({
  userBuilding,
  userRole,
  selectedBuilding,
  isAnalyticsVisible,
  setNewReservationFormVisible = () => {},
  toggleAnalyticsVisibility = () => {},
  setAddTimeBlockModalVisible = () => {},
  setAmenityMarketplaceModalVisible = () => {},
}) => {
  const { t } = useTranslation(['reservations', 'common'])
  const isBuildingSelected = userBuilding || selectedBuilding
  const navigate = useNavigate()
  const location = useLocation()
  const { captureEvent } = useCaptureEvent()

  const prefix = '/dashboard/building-dashboard/reservations'

  const isBookingsRoute = useMatch(`${prefix}/bookings/reservations-list`)
  const isAmenitiesRoute = useMatch(`${prefix}/amenities`)
  const isEditAmenityRoute = useMatch(`${prefix}/amenities/edit/:amenityId`)
  const isCreateAmenityRoute = useMatch(`${prefix}/amenities/create`)

  const handleUIToggle = () => {
    const newPath = isAmenitiesRoute ? NEW_AMENITIES_PATH : NEW_RESERVATIONS_PATH
    captureEvent('user_action', {
      from_path: location.pathname,
      name: 'design_toggle',
      screen: 'Reservations',
      to_path: newPath,
    })
    navigate(newPath)
  }

  const amenityId = isEditAmenityRoute?.params?.amenityId
  const amenityName = useSelector((state) =>
    amenityId ? selectAmenityById(state)(amenityId)?.name : undefined,
  )

  const handleBackToAmenities = () => {
    const fallbackPath = isProductionBuild ? `${prefix}/amenities` : NEW_AMENITIES_PATH
    const destination = location.key !== 'default' ? -1 : fallbackPath
    if (window.createAmenityFormNavigation) {
      window.createAmenityFormNavigation(destination)
    } else {
      navigate(destination)
    }
  }

  let currentView = null

  const routes = [
    {
      name: t('reservations:reservationsToolbar.dashboard'),
      path: '/dashboard/building-dashboard/reservations/bookings/reservations-list',
    },
    {
      name: t('reservations:reservationsToolbar.detailedView'),
      path: '/dashboard/building-dashboard/reservations/detailed-view',
      hidden: true,
    },
    {
      name: t('reservations:reservationsToolbar.amenities'),
      path: '/dashboard/building-dashboard/reservations/amenities',
      hidden: userRole === 'ShiftManager',
    },
  ]

  if (isBookingsRoute || isAmenitiesRoute) {
    currentView = (
      <>
        <div className="reservations-top-navbar reservations-top-navbar--between">
          <div className="header-section">
            <div className="header">{t('reservations:reservationsToolbar.reservations')}</div>
          </div>
          <div className="header-buttons">
            {/* TODO: remove the production gate once the new Reservations pages launch. */}
            {!isProductionBuild && (
              <ToggleSwitch
                falsyLabel="Old Design"
                truthyLabel="New Design"
                checked={false}
                onChange={handleUIToggle}
              />
            )}
            <button
              className="button button-blue"
              onClick={setAmenityMarketplaceModalVisible}
              disabled={!isBuildingSelected}
            >
              <LightningIcon className="reservations-toolbar-menu-icon" size={20} weight="fill" />
              {t('reservations:reservationsToolbar.generateNewIncome')}
            </button>
            <button
              className="button button-transparent"
              onClick={setAddTimeBlockModalVisible}
              disabled={!isBuildingSelected}
            >
              <ClockIcon weight="regular" />
              {t('reservations:reservationsToolbar.addTimeBlock')}
            </button>
            <button
              className="button button-primary"
              onClick={() => setNewReservationFormVisible(true)}
              disabled={!isBuildingSelected}
            >
              <PlusCircleIcon weight="regular" />
              {t('reservations:reservationsToolbar.addReservation')}
            </button>
          </div>
        </div>
        <ToolbarMenu
          routes={routes}
          isAnalyticsVisible={isAnalyticsVisible}
          isBuildingSelected={isBuildingSelected}
          toggleAnalyticsVisibility={toggleAnalyticsVisibility}
        />
      </>
    )
  } else if (isCreateAmenityRoute) {
    currentView = (
      <div className="reservations-top-navbar">
        <div className="header-buttons">
          <button onClick={handleBackToAmenities} className="button button-lightgray">
            <ArrowLeftIcon />
            {t('common:actions.back')}
          </button>
        </div>
        <div className="header-section">
          <div className="header">{t('reservations:emptyState.createNewAmenity')}</div>
        </div>
      </div>
    )
  } else if (isEditAmenityRoute) {
    currentView = (
      <div className="reservations-top-navbar reservations-top-navbar--between">
        <div className="header-buttons">
          <button className="button button-lightgray" onClick={handleBackToAmenities}>
            <ArrowLeftIcon />
            {t('common:actions.back')}
          </button>
          <div className="header-section">
            <div className="header">
              {amenityName || t('reservations:reservationsToolbar.editAmenity')}
            </div>
          </div>
        </div>
        <div className="header-buttons">
          <button
            className="button button-lightgray delete-amenity-btn"
            onClick={() => {
              if (window.deleteAmenityHandler) {
                window.deleteAmenityHandler()
              }
            }}
          >
            <TrashIcon size={20} />
            {t('reservations:amenityForms.deleteAmenity.title')}
          </button>
        </div>
      </div>
    )
  }

  return <div className="reservations-toolbar">{currentView}</div>
}

ReservationsToolbar.propTypes = {
  userRole: PropTypes.string,
  userBuilding: PropTypes.string,
  selectedBuilding: PropTypes.object,
  isAnalyticsVisible: PropTypes.bool,
  setNewReservationFormVisible: PropTypes.func,
  toggleAnalyticsVisibility: PropTypes.func,
  setAddTimeBlockModalVisible: PropTypes.func,
  setAmenityMarketplaceModalVisible: PropTypes.func,
}

export default ReservationsToolbar
